<template>
  <div :class="[containerClass, hasIcon(icon)]">
    <label v-if="label" :id="`input-${name}-label`" :for="`input${name}`" :class="[labelClass || 'form-label fs-6 ', {isRequired: 'required'}]">
      {{ label }} <span v-if="isRequired" class="text-danger"> * </span>
    </label>
    <div  :class="IsInputGroup ? 'input-group' : ''">
       <span v-show="iconDir === 'left'" :id="`date-input-${name}-addon`" class="input-group-text" :class="addonClass">
       {{ iconText }}
        <i :class="getIcon(icon)"></i>
       </span>
      <input
          class="form-control"
          :class="[{ 'invalid': (meta.touched && !meta.valid) }, getClasses(size), inputClass]"
          :name="name"
          :id="`date-input-${name}`"
          v-model="value"
          :placeholder="$t(dateFormat)"
          :required="isRequired"
          :aria-label="label"
          :aria-describedby="`input-${name}-addon`"
          :disabled="isDisabled"
          :rules="rules"
          autocomplete="off"
          
      />
      <span v-show="iconDir === 'right'" :id="`date-input-${name}-addon`" class="input-group-text" :class="addonClass">
         {{ iconText }}
          <i :class="getIcon(icon)"></i>
      </span>
    </div>
  </div>
</template>

<script setup>
import { useLocales } from '@/composables/locales';
import { useField } from 'vee-validate';
import {nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";

const props = defineProps( {
  size: {
    type: String,
    default: "default",
  },
  parentId: { type: String, default: '' },
  icon: String,
  iconDir: String,
  name: String,
  id: String,
  modelValue: String,
  placeholder: String,
  isRequired: Boolean,
  inputClass: {type:String, default: "form-control form-control-solid"},
  labelClass: String,
  containerClass: {
    type: String,
    default: 'col-12 mb-10'
  },
  label: String,
  isDisabled: Boolean,
  rules: String,
  iconText: String,
  IsInputGroup: Boolean,
  addonClass: String,
  disabled: Array,
  dateFormat: {type: String, default: "d-m-Y"},
  minDate: String,
  maxDate: String,
  showLabel: { type : Boolean, default: true}
})

const {locale} = useLocales()

const datepicker = ref('')
const { handleChange, value, handleBlur, errorMessage, meta } = useField(() => props.name, undefined, {
  syncVModel: true,
});


onBeforeUnmount(() => {
  if (datepicker.value) {
    datepicker.value.destroy();
  }
});

watch(() => locale.value, (value) => {
  if(value) {
    nextTick(() => {
      initDataPicker()
    })
  }
})

watch(() => props.modelValue, (value) => {
  if(value) {
    nextTick(() => {
      initDataPicker()
    })
  }
})

watch(() => [props.minDate, props.maxDate], (value) => {
  if(value) {
    nextTick(() => {
      initDataPicker()
    })
  }
})

function getClasses (size) {
  let sizeValue, isValidValue;

  sizeValue = size ? `form-control-${size}` : null;

  isValidValue = meta.touched && !meta.valid ? `invalid` : "valid";

  return `${sizeValue} ${isValidValue}`;
}
function getIcon (icon) {
  return icon ? icon : null
}
function hasIcon (icon) {
  return icon ? "input-group" : null
}

const fr =  {
    firstDayOfWeek: 1,
    weekdays: {
        shorthand: ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
        longhand: [
            "dimanche",
            "lundi",
            "mardi",
            "mercredi",
            "jeudi",
            "vendredi",
            "samedi",
        ],
    },
    months: {
        shorthand: [
            "janv",
            "févr",
            "mars",
            "avr",
            "mai",
            "juin",
            "juil",
            "août",
            "sept",
            "oct",
            "nov",
            "déc",
        ],
        longhand: [
            "janvier",
            "février",
            "mars",
            "avril",
            "mai",
            "juin",
            "juillet",
            "août",
            "septembre",
            "octobre",
            "novembre",
            "décembre",
        ],
    },
    ordinal: function (nth) {
        if (nth > 1)
            return "";
        return "er";
    },
    rangeSeparator: " au ",
    weekAbbreviation: "Sem",
    scrollTitle: "Défiler pour augmenter la valeur",
    toggleTitle: "Cliquer pour basculer",
    time_24hr: true,
}

const en =  {
      weekdays: {
          shorthand: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
          longhand: [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
          ],
      },
      months: {
          shorthand: [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
          ],
          longhand: [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
          ],
      },
      daysInMonth: [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
      firstDayOfWeek: 0,
      ordinal: function (nth) {
          var s = nth % 100;
          if (s > 3 && s < 21)
              return "th";
          switch (s % 10) {
              case 1:
                  return "st";
              case 2:
                  return "nd";
              case 3:
                  return "rd";
              default:
                  return "th";
          }
      },
      rangeSeparator: " to ",
      weekAbbreviation: "Wk",
      scrollTitle: "Scroll to increment",
      toggleTitle: "Click to toggle",
      amPM: ["AM", "PM"],
      yearAriaLabel: "Year",
      monthAriaLabel: "Month",
      hourAriaLabel: "Hour",
      minuteAriaLabel: "Minute",
      time_24hr: false,
  }

function initDataPicker() { 
  if(datepicker.value){
    datepicker.value.destroy()
  }
  
  datepicker.value = $("#date-input-" + props.name).flatpickr({
    onReady: function () {
      const initDate = props.modelValue ? new Date(props.modelValue) : new Date()
      this.jumpToDate(initDate)
    },
    dateFormat: props.dateFormat || "d-m-Y",
    disable: props.disabled || [],
    minDate: props.minDate || '',
    maxDate: props.maxDate || '',
    allowInput: true,
    enableTime: props.dateFormat?.includes("H:i"),
    locale: locale.value == "fr" ? fr : en ,
  })

  
}

onMounted(() => {
  initDataPicker()
})

</script>
<style scoped>
input.invalid {
  border: 1px solid red;
}
</style>
